import type {
  DeliveryAddressModel,
  DeliveryAddressReqModel,
  PaymentAddressModelV4, PaymentAddressReqModel,
  PaymentReqModelV4,
} from '@develop/ia-api-library'
import {
  payWithPayPalV4 as payWithPaypalAPI,
  setPaymentV4 as setPaymentAPI,
} from '@develop/ia-api-library'
import { defineStore } from 'pinia'
import type { CheckoutPaymentModelV2, PaymentMethodModel, PaymentProviderModel } from '@/models/checkout-payment-model-v2'

export const usePaymentStore = defineStore('payment', () => {
  const authStore = useAuthStore()
  const checkoutStore = useCheckoutStore()
  const deliveryStore = useDeliveryStore()
  const anonymousUser = useAnonymousUser()

  const { locale } = useTranslation()

  const DEFAULT_PAYMENT: CheckoutPaymentModelV2 = {
    address: null,
    isIdenticalToDeliveryAddress: false,
    method: null,
  }

  const paymentData = ref<CheckoutPaymentModelV2>(DEFAULT_PAYMENT)

  const paymentConfig = computed(() => checkoutStore.checkoutStatus?.payment)

  const anonymousUserId = computed(() => authStore.token ? undefined : anonymousUser.getToken())

  const isLoading = ref(false)

  const setAddress = (address: PaymentAddressModelV4 | null) =>
    set(paymentData, { ...paymentData.value, address })

  const setIsIdenticalToDeliveryAddress = (isIdenticalToDeliveryAddress: boolean) =>
    set(paymentData, { ...paymentData.value, isIdenticalToDeliveryAddress })

  const convertDeliveryAddressToPaymentAddress = (deliveryAddress: DeliveryAddressModel | DeliveryAddressReqModel) => {
    const { salutation, firstName, lastName, streetName, houseNumber, cityName, zipCode } = deliveryAddress
    const convertedAddress = {
      salutation: salutation || null,
      firstName: firstName ?? '',
      lastName: lastName ?? '',
      streetName: streetName ?? '',
      houseNumber: houseNumber ?? '',
      cityName: cityName ?? '',
      zipCode: zipCode ?? '',
      additionalPaymentInfo: null,
    }
    return convertedAddress
  }

  const setMethod = (method: PaymentMethodModel) =>
    set(paymentData, { ...paymentData.value, method })

  const setFundingSource = (fundingSource: string) =>
    set(paymentData, { ...paymentData.value, fundingSource })

  const isOnlinePaymentSelected = computed(() => !!paymentConfig.value?.selected.paymentProvider)

  const hasPaymentOptions = computed(() => {
    const paymentOptions = paymentConfig.value?.options
    if (!paymentOptions)
      return false

    return Object.values(paymentOptions).some(option => option?.allowed)
  })

  const confirmPayment = async (paymentProvider: PaymentProviderModel, hasSameDeliveryAndPaymentAddress?: boolean, newPaymentAddressId?: number) => {
    if (!checkoutStore.draftId) {
      onShowLogs(() => console.log('Draft ID is missing.'))
      return false
    }

    const { token, refreshToken } = authStore
    const options = {
      token: token ?? '',
      refreshToken: refreshToken ?? '',
      language: locale,
    }

    const { address } = paymentData.value

    let payload: PaymentReqModelV4

    // Check for missing paymentProvider or address early
    if (!paymentProvider || !address) {
      payload = {
        anonymousUserId: anonymousUserId.value,
        paymentOption: {
          paymentProvider: null,
        },
      }
    } else {
      const addressId = 'id' in address ? (address.id as number) : null

      // Used for guest users when the payment address is the same as the delivery address
      let convertedAddress: PaymentAddressReqModel | null = null
      const deliveryAddress = deliveryStore.deliveryData.address
      if (hasSameDeliveryAndPaymentAddress && deliveryAddress && !authStore.isAuthenticated) {
        convertedAddress = convertDeliveryAddressToPaymentAddress(deliveryAddress)
      }

      payload = {
        anonymousUserId: addressId ? undefined : anonymousUserId.value,
        paymentOption: {
          paymentProvider,
          fundingSource: paymentProvider as string,
          ...(addressId
            ? { paymentAddressId: newPaymentAddressId ?? addressId }
            : { address: convertedAddress || address }
          ),
        },
      }
    }

    set(isLoading, true)
    const { success, data } = await setPaymentAPI(payload, checkoutStore.draftId, options)
    set(isLoading, false)

    if (success && data)
      checkoutStore.setCheckoutStatus(data)
  }

  const payWithPaypal = async () => {
    if (!checkoutStore.draftId) {
      onShowLogs(() => console.log('Draft ID is missing.'))
      return false
    }

    const { token, refreshToken } = authStore
    const options = {
      token: token ?? '',
      refreshToken,
      language: locale,
    }

    const payload = {
      anonymousUserId: anonymousUserId.value,
      context: 'testing_web',
    }

    const { success, data } = await payWithPaypalAPI(payload, checkoutStore.draftId, options)

    if (success && data)
      return data.approvalUrl

    return null
  }

  const reset = () => set(paymentData, DEFAULT_PAYMENT)

  return {
    paymentData,
    isOnlinePaymentSelected,
    hasPaymentOptions,
    setAddress,
    setIsIdenticalToDeliveryAddress,
    convertDeliveryAddressToPaymentAddress,
    setMethod,
    setFundingSource,
    confirmPayment,
    payWithPaypal,
    isLoading,
    reset,
  }
})
